import { REMOTE_ACTIONS, REMOTE_BUTTONS, actionLabel, type RemoteActionId } from "../remote/remoteMap.js";
import { SLEEP_CHOICES, sleepLengthShort } from "../player/sleepTimer.js";

/**
 * The Help section's words (feature-plan §11, launch guide G9: "an in-app help area inside
 * Settings, so the answers live where the questions arise instead of only in documents nobody
 * watching a TV will read").
 *
 * Two rules this file follows:
 *
 *  1. It describes the app that exists. Every screen, card and button named here is read from
 *     the source that renders it (helpTopics.test.ts fails when something is missing), and
 *     nothing that is still only planned appears — a Help page that promises unbuilt features
 *     is worse than none.
 *  2. The remote section is GENERATED from `remote/remoteMap.ts`, the same table the app
 *     actually dispatches through, so a button that is added or renamed cannot go undescribed.
 *
 * Plain strings, no JSX, so the words are easy to edit without touching the page.
 */

export interface HelpSection {
  /** A heading inside the topic. Omitted for the opening paragraph. */
  heading?: string;
  /** Standalone sentences, one idea each. */
  paragraphs?: string[];
  /** One line per thing: a button, a step, a caveat. */
  bullets?: string[];
}

export interface HelpTopic {
  id: string;
  title: string;
  /** One line in the topic list. Kept short: it is read from across a room. */
  summary: string;
  /** 24×24 stroke icon path, the same shape as SettingsPage's SETTINGS_LINKS. */
  icon: string;
  sections: HelpSection[];
}

const ICONS = {
  tv: "M3 7h18v12H3zM8 3l4 4 4-4",
  remote: "M9 3h6v18H9zM12 7h.01M12 11h.01M12 15h.01",
  guide: "M3 5h18v14H3zM9 5v14M3 10h18",
  play: "m10 9 5 3-5 3zM3 5h18v14H3z",
  gear: "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM12 2v3M12 19v3M4 12H2M22 12h-2M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2",
  grid: "M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z",
  clock: "M12 7v5l3.5 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z",
  record: "M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z",
  home: "M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5",
  film: "M7 5v14M17 5v14M3 9h4M3 15h4M17 9h4M17 15h4",
  star: "m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.3l6.1-.7z",
  people: "M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2 20a7 7 0 0 1 14 0M16 4.1a3.5 3.5 0 0 1 0 6.8M18 13.5a7 7 0 0 1 4 6.5",
  lock: "M12 3a4 4 0 0 1 4 4v3H8V7a4 4 0 0 1 4-4zM6 10h12v10H6z",
  keyboard: "M3 6h18v12H3zM7 10h.01M11 10h.01M15 10h.01M7 14h10",
  phone: "M8 2h8v20H8zM12 18h.01",
  bell: "M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9zM13.7 21a2 2 0 0 1-3.4 0",
  sync: "M20 12a8 8 0 0 1-13.7 5.6M4 12a8 8 0 0 1 13.7-5.6M3 17v-4h4M21 7v4h-4",
  card: "M2 6h20v12H2zM2 10h20",
  alert: "M12 3v9M6.3 6.3a8 8 0 1 0 11.4 0",
  sign: "M4 7h16M4 11h16M4 15h12M4 19h8",
};

/**
 * The watching behaviour of a button, in the viewer's words: `pressText` where the action's
 * name would not say it all (Left is "Rewind 10 seconds", not "Back 10 seconds"), otherwise
 * the action's own label from REMOTE_ACTIONS.
 */
function pressWords(action: RemoteActionId, pressText?: string): string {
  return pressText ?? actionLabel(action);
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** One line per remote button: what it does while watching, and what it does in the menus. */
function remoteButtonBullets(): string[] {
  return REMOTE_BUTTONS.map((button) => {
    const watching = button.watchingOnly
      ? `While watching full screen: ${lowerFirst(pressWords(button.press.action, button.pressText))}`
      : pressWords(button.press.action, button.pressText);
    return `${button.label} — ${watching}. In the menus: ${lowerFirst(button.inMenus)}.`;
  });
}

/** Every action a button can be given, grouped the way the picker groups them. */
function remoteActionBullets(group: "navigation" | "player"): string[] {
  return REMOTE_ACTIONS.filter((action) => action.group === group).map((action) => action.label);
}

function sleepChoicesList(): string {
  return SLEEP_CHOICES.map((minutes) => sleepLengthShort(minutes)).join(", ");
}

export const HELP_TOPICS: readonly HelpTopic[] = [
  {
    id: "install",
    title: "Getting the app onto a TV",
    summary: "Sideload it with the Downloader code, or install it from a browser.",
    icon: ICONS.tv,
    sections: [
      {
        paragraphs: [
          "A TV box cannot open an app store page you tap like a phone, so the app is installed by hand once, with the Downloader app.",
        ],
      },
      {
        heading: "On an Android TV, Google TV or Fire TV box",
        bullets: [
          "Install Downloader from the box's own store (on Fire TV, search for it in the App Store), then open it.",
          "Type the code 4019573, or the address sliquetv.online/sliqueplayer, into Downloader's address bar.",
          "Let it download, choose Install, then Open. The app appears in your box's apps from then on.",
          "Updates are the same file over the top: open the same address again and install. Your source, profiles, favourites and recordings are kept.",
          "Or, in the app, open Settings → App updates and press Check for updates. Android asks before it installs.",
        ],
      },
      {
        heading: "On a phone, tablet or laptop",
        bullets: [
          "Open sliquetv.online/sliqueplayer in Chrome or Android's browser and press Install app in the top bar.",
          "On an iPhone or iPad, the browser cannot install for you: open the page in Safari, press Share, then Add to Home Screen.",
          "The website has no recording, no multiview and no phone remote — those live in the app.",
        ],
      },
      {
        heading: "Good to know",
        bullets: [
          "The download link is a plain file, so anyone with the link can fetch it. Keep it to your own family and devices.",
          "A Google Play listing is on its way. Until it is up, this is the way in.",
        ],
      },
    ],
  },
  {
    id: "connect-source",
    title: "Connect your streaming source",
    summary: "Where your channels, movies, series and guide come from.",
    icon: ICONS.sign,
    sections: [
      {
        paragraphs: [
          "The app has no channels of its own: everything you watch comes from the provider you already pay for. On a fresh install the first screen asks for it, and afterwards it lives in Settings → Source.",
        ],
      },
      {
        heading: "Xtream Codes (the usual one)",
        bullets: [
          "Choose Xtream Codes, then fill in Server, Username and Password from your provider's welcome email.",
          "Server is the address only — no http:// and no port, unless your provider's own instructions say otherwise.",
        ],
      },
      {
        heading: "An M3U playlist",
        bullets: [
          "Choose M3U, then paste the playlist URL your provider gave you.",
          "If your provider names a guide (EPG) address in the same email, put it in the EPG field; leave it empty to use the one the playlist names for itself.",
        ],
      },
      {
        heading: "After it connects",
        bullets: [
          "Your settings are saved on this device as you type. Nothing goes to us.",
          "Add a second source (a backup server, or a relative's login) and switch between them from the same page. One playlist is free, more are part of Pro.",
          "Remove Active takes away the one you are using; press it twice to confirm. The first-run screen comes back if it was your only source.",
          "The first connect also downloads the guide, which is the big one: a first install can spend a couple of minutes on it, with a loading line to say so. It is quick afterwards.",
        ],
      },
    ],
  },
  {
    id: "remote",
    title: "The remote: every button",
    summary: "What each button on your remote does, in the menus and while watching.",
    icon: ICONS.remote,
    sections: [
      {
        paragraphs: [
          "The arrow keys and OK move around every screen and select what is ringed; Back always steps back — it closes what is open, then goes to the screen before. Nothing else on the remote is needed to use the app.",
          "You can change what most buttons do in Settings → Remote control. This is what they do untouched.",
        ],
      },
      { heading: "The buttons", bullets: remoteButtonBullets() },
      {
        heading: "What a button can be set to",
        paragraphs: ["Everything a button can be given, if you would rather have it somewhere else:"],
        bullets: [
          ...remoteActionBullets("navigation"),
          ...remoteActionBullets("player"),
          "Not set (the button does nothing) — for a button you would rather switch off entirely.",
        ],
      },
      {
        heading: "Small notes",
        bullets: [
          "The arrows and OK can only be changed while watching something full screen. In the menus they always navigate, because that is how you get anywhere.",
          "A button held down is a long press, and it can do something different from a press.",
          "No button starts with a sleep timer; set one on any button you do not use, or use the player's own menu.",
        ],
      },
    ],
  },
  {
    id: "guide",
    title: "Live TV and the guide",
    summary: "The channel list, the grid, the keypad and the channel menu.",
    icon: ICONS.guide,
    sections: [
      {
        paragraphs: [
          "Live TV is one screen: the channel list down the left, the schedule grid beside it, and your picture where the layout puts it. Up and Down walk the channels, Left and Right walk the time, and OK plays what is ringed.",
        ],
      },
      {
        heading: "The two controls at the top",
        bullets: [
          "Category — the dropdown that filters the list to one group of channels (Sports, Movies, your provider's own bundles). All categories puts everything back.",
          "Channel number entry — opens a keypad. Type the number, then ✓ Tune to jump straight to that channel; ⌫ Clear wipes what you typed.",
          "⛶ Full screen — with the Overlay layout it leaves the guide over the picture and goes full screen.",
        ],
      },
      {
        heading: "On a programme",
        bullets: [
          "OK plays the programme's channel.",
          "Hold OK for the channel's menu: Watch, Add channel to favorites, Hide this channel, Record…, Programme details, Mirror for fallback… and, in the TV app, Choose guide….",
          "Programme details opens the full description, episode and cast information where your provider sends it.",
          "Add channel to favorites is what the ❤ on your remote does too, and Mirror for fallback… links a backup feed of the same channel from another of your sources, so a dead primary still has something to fall back on.",
          "Choose guide… fixes a channel showing the wrong programmes: search the guide's channels (from this playlist's guide and its extra guide sources) and pick the right one, or go back to Automatic. Its Time shift moves the channel's programmes earlier or later, a quarter hour at a time, for a guide that is off by an hour or a +1 channel.",
          "Hiding a channel takes it out of the list everywhere in the app. Unhide this channel puts it back.",
        ],
      },
      {
        heading: "Zapping",
        bullets: [
          "Channel Up and Channel Down change channel straight away, and a small banner names what you landed on.",
          "While a channel changes, the picture shows a starting card (the channel's logo, its name and Tuning…) instead of a flash of the guide.",
        ],
      },
      {
        heading: "Making the guide yours",
        bullets: [
          "Settings → Guide sets the layout (Traditional puts a preview window above the grid; Overlay draws the grid over the picture), the transparency, the text size and how many hours are shown.",
          "In Traditional, Preview window: OK for full screen jumps out of the guide into the picture; Back comes back.",
          "The guide keeps only the category picker and the channel box, so there is less to press past on the way to a channel.",
        ],
      },
      {
        heading: "While the guide downloads",
        bullets: [
          "A line at the top says Loading the guide… and how many MB have arrived, with a bar. It is not a button, so it never gets in the way of the arrows.",
          "If the guide looks empty or short on a new install, leave the app open a minute or two and it fills in.",
        ],
      },
    ],
  },
  {
    id: "player",
    title: "Player controls",
    summary: "Every button on the bar over the picture, and what comes up from them.",
    icon: ICONS.play,
    sections: [
      {
        paragraphs: [
          "Playing something fills the screen, and pressing a button brings up the bar. Press again (or wait a few seconds) and it slides away, leaving the picture clean.",
        ],
      },
      {
        heading: "The bar",
        bullets: [
          "⚙ Player settings — the menu in the corner: picture shape, subtitles, sound track and speed. Press again, or Back, to close it.",
          "⏪ Rewind 10 seconds / ⏩ Fast forward 30 seconds — with pause and rewind live TV turned on, these move through the hour you have saved. Hold the arrow keys or the remote's rewind and fast-forward for the same thing.",
          "● LIVE / Back to live — appears once you are behind, and jumps to the live edge.",
          "⏺ Record / Stop recording — starts recording what you are watching; while it records it shows the size, and pressing it again stops it. It asks where to save the first time.",
          "⇄ Previous channel — back to the one you were on, or the last channel you watched. It says No previous channel when there is not one yet.",
          "▤ Recent channels / Hide recent channels — the last channels you watched, as a strip of tiles over the picture. Press it again to put the strip away.",
          "▶ / ❚❚ Play and pause.",
          "ⓘ Programme info — what is on now and what is next, with the times, how far through it is and the description. The remote's Info button opens the same thing.",
          "⧉ Picture in picture / Exit picture in picture — keeps the picture going in a small window over other apps, on boxes that support it.",
          "⊞ Multiview — the four-channel grid, on live TV only.",
          "↻ Retry stream — appears when a stream fails, and tries it again.",
          "✕ Close player — stops playback and goes back to what you were looking at.",
        ],
      },
      {
        heading: "The bars",
        bullets: [
          "A film or an episode has a seek bar: drag it, or use Left and Right, which jump 10 seconds back and 30 seconds forward.",
          "Live TV with pause and rewind turned on has a Rewind live TV bar under the title instead, showing how far behind live you are.",
          "The clock and, if one is set, Sleep 42 min sit in the corner and fade out with the buttons.",
          "Next episode — a countdown appears at the end of an episode; OK jumps straight to the next one.",
        ],
      },
      {
        heading: "When a stream will not start",
        bullets: [
          "The picture itself says what failed, in plain words, with the retry button named underneath. It is not hidden in the bar.",
          "A channel that fails twice is marked as not responding and hidden from the guide by itself; Settings → Sync & Health shows which, and why.",
        ],
      },
    ],
  },
  {
    id: "player-settings",
    title: "Player settings: picture, sound, subtitles",
    summary: "The gear ⚙ menu: aspect ratio, subtitle tracks, audio tracks, speed.",
    icon: ICONS.gear,
    sections: [
      {
        paragraphs: [
          "The gear in the corner of the picture opens the player's own menu. What it offers comes from the stream now playing, so a channel without subtitles shows none.",
        ],
      },
      {
        heading: "Aspect ratio",
        bullets: [
          "Fit — the picture's own shape, with bars where it does not fill the screen.",
          "Fill — zoomed until the bars are gone, so the edges are cropped.",
          "Stretch — stretched to the screen, which distorts the picture.",
          "Zoom — in a third, to crop bars that are part of the picture itself.",
        ],
      },
      {
        heading: "Subtitles",
        bullets: [
          "Off, or any track the stream carries, by name (English, Forced, and so on).",
          "This stream carries no subtitles appears when there are none, rather than an empty list.",
          "What you choose is remembered for that channel and that profile, so the next time you tune it your choice is already on.",
        ],
      },
      {
        heading: "Audio",
        bullets: [
          "Every sound track the stream carries, shown when there is more than one (English, Spanish, and commentary tracks).",
          "The app decodes AC3, E-AC3 and DTS itself, which is what makes the multi-channel sound on live channels work.",
          "Like subtitles, your choice is remembered per channel.",
        ],
      },
      {
        heading: "Speed and sleep",
        bullets: [
          "Speed — 0.5× to 2×, for films and episodes only. Live TV can only be normal speed.",
          `Sleep timer — the same timer as in Settings → Player: Off, or ${sleepChoicesList()}.`,
        ],
      },
    ],
  },
  {
    id: "multiview",
    title: "Multiview: four channels at once",
    summary: "The ⊞ grid, why it sometimes stops at two, and how to swap a tile.",
    icon: ICONS.grid,
    sections: [
      {
        paragraphs: [
          "⊞ on the player bar turns live TV into a grid of up to four channels watching at the same time: the game in the corner, the news in another, all live.",
        ],
      },
      {
        heading: "The grid",
        bullets: [
          "Search channels to add… finds a channel by name or number; the list puts mainstream networks first.",
          "Hide channels / Show channels gives the tiles the whole screen, or brings the list back.",
          "MAIN marks the tile you hear; ⭐ Make main moves that to another tile. The others stay muted so they can start without a press.",
          "✕ Remove takes a tile away, and ↻ Retry starts a tile whose channel dropped.",
          "Back or ✕ Exit leaves the grid and returns to the channel you were on.",
        ],
      },
      {
        heading: "Why it can stop at two or three",
        paragraphs: [
          "A provider sells a number of streams at once, usually one or two, and recordings use one each. The grid says which limit it hit:",
        ],
        bullets: [
          "Your provider allows N streams at a time — that is the account's limit.",
          "a recording is using one — a recording always wins over a tile.",
          "N are in use on another device — someone else is watching on the same account.",
          "This device can play N at once — the box itself ran out of capacity.",
          "Grid full — remove a tile to swap channels.",
        ],
      },
      {
        heading: "Also worth knowing",
        bullets: [
          "Multiview is live TV only: a film plays on its own.",
          "It is part of Pro. Free viewers can still watch one channel, or use picture in picture.",
          "Each tile is its own stream, so a provider with a two-stream account can run two, not four.",
        ],
      },
    ],
  },
  {
    id: "timeshift",
    title: "Pause and rewind live TV",
    summary: "The hour behind the live edge, and how to turn it on.",
    icon: ICONS.clock,
    sections: [
      {
        paragraphs: [
          "Live TV can be paused, rewound and fast-forwarded as you watch it, up to an hour back. It is on by default in the app, and it is part of Pro.",
        ],
      },
      {
        heading: "Turning it on and off",
        bullets: [
          "Settings → Player → Pause and rewind live TV.",
          "Keep the live TV buffer on chooses which drive holds it, from the same list recordings use — a USB drive gives the full hour, the box's own storage less.",
          "If a drive is nearly full the setting says how many minutes of HD still fit.",
        ],
      },
      {
        heading: "Using it",
        bullets: [
          "Press pause on the bar, or the remote's pause, to stop the picture.",
          "⏪ and ⏩ move 10 seconds back and 30 seconds forward; ● LIVE returns to the live edge, and the title shows how far behind you are.",
          "The buffer starts again when you change channel.",
          "It is never counted as a recording, and it gives way to recordings when space runs low — a recording you asked for always has room.",
        ],
      },
    ],
  },
  {
    id: "catchup",
    title: "Catch-up: watching what already aired",
    summary: "The provider's archive of the last few days, channel by channel.",
    icon: ICONS.clock,
    sections: [
      {
        paragraphs: [
          "Catch-up is your provider's own archive: programmes that already aired, kept for a few days, ready to play. It is not a recording — nothing is saved on your device. Catch-up is part of Pro.",
        ],
      },
      {
        heading: "Watching something back",
        bullets: [
          "Catch-up in the sidebar lists the channels your provider keeps an archive for, with a Days picker across the top.",
          "Choose a day, then a programme, and it plays.",
          "In the guide, a programme that has already started on an archived channel offers Watch again.",
          "Nothing to replay on this day means the provider has no copy of it — which is common for sport and for channels the provider does not archive.",
        ],
      },
      {
        heading: "Catch-up, a recording, or the live buffer",
        bullets: [
          "Catch-up: the provider's copy of a programme, from hours or days ago, for channels they archive.",
          "A recording: your own copy, on your device, that you can keep for as long as you like.",
          "The live buffer: what is on now, up to an hour back, and it disappears when you change channel.",
        ],
      },
    ],
  },
  {
    id: "recordings",
    title: "Recording and scheduled recordings",
    summary: "Record what you are watching, or a whole series, on this device.",
    icon: ICONS.record,
    sections: [
      {
        paragraphs: [
          "Recordings are made and kept on this device, so they play back without the provider. Recording is part of Pro.",
        ],
      },
      {
        heading: "Starting one",
        bullets: [
          "⏺ on the player bar records what you are watching right now.",
          "In the guide, hold OK on a programme for its menu, then Record…. A film or episode's own page has the same option.",
          "The dialog asks what to record, which channel, which episodes and how many to keep.",
          "Which episodes: just this one, every airing, or only new episodes.",
          "How many to keep: Keep all, or a limit, so a series cannot fill the drive.",
        ],
      },
      {
        heading: "The Recordings screen",
        bullets: [
          "Recording now — a recording in progress, with ⏹ Stop.",
          "Scheduled — programmes waiting to start, with Cancel on each.",
          "Series — the rules you have set, with Stop series when you are done with the show.",
          "Coming up — the next episodes a series rule has found, and Skip for one you do not want.",
          "Recorded — what you have kept, with its size; Delete asks to confirm first.",
          "Didn't record — anything that failed, and why, with Dismiss.",
          "Save recordings to — which drive recordings go to. A USB drive holds far more than the box.",
          "Allow alarms asks Android for permission to wake the box; a recording that would be missed while it is asleep needs it.",
          "Check now asks the guide again for episodes of the series you are following, and the app also checks by itself every couple of hours.",
        ],
      },
      {
        heading: "Space",
        bullets: [
          "The dialog shows the free space it needs before it books anything.",
          "A recording can take the last stream your provider allows, so multiview may not open four tiles while one is running.",
        ],
      },
    ],
  },
  {
    id: "home",
    title: "Home and its rows",
    summary: "The featured banner, Continue Watching, Live Now and the rest.",
    icon: ICONS.home,
    sections: [
      {
        paragraphs: [
          "Home is a set of rows built from your own provider plus open data, so it looks right on the first launch rather than after weeks of use.",
        ],
      },
      {
        heading: "What is on it",
        bullets: [
          "The featured banner at the top: a title with its artwork, rating, description and a Play button, rotating every minute.",
          "Continue Watching — anything you started and have not finished, with a progress bar. Continue picks it up where you left off.",
          "Recently Watched — channels you tuned, showing what is on them now.",
          "Live Now — what is on now across your channels, matched by the guide.",
          "Recommended For You, Trending and Popular rows, with See all for the full list.",
          "My List — your favorite channels, movies and series.",
        ],
      },
      {
        heading: "Slowing it down",
        paragraphs: [
          "On a small box these rows are the most expensive thing the app draws. Settings → Home turns them down:",
        ],
        bullets: [
          "Banner trailer — plays the featured title's trailer behind the banner. Off is quicker.",
          "Live Now row — the external row. Off is quicker.",
          "Rotating featured title — change the banner every minute, or leave it still.",
          "Titles per row — 12 or 24. Shorter rows draw faster.",
          "Start on — Home, or Live TV (see the topic about TV-style start).",
        ],
      },
    ],
  },
  {
    id: "movies-series",
    title: "Movies, Series and a title's page",
    summary: "Browsing the catalogue, and everything on a title you open.",
    icon: ICONS.film,
    sections: [
      {
        paragraphs: [
          "Movies and Series show your provider's own catalogue with artwork, sorted and searchable, filtered by genre. A title opens a page with everything known about it.",
        ],
      },
      {
        heading: "Browsing",
        bullets: [
          "The search box narrows the list as you type.",
          "Sort movies: Most popular, Newest year or Recently added. Series sort the same way.",
          "Filter by genre, or All genres, from the dropdown beside it.",
          "A very large catalogue shows a capped grid: use search to narrow it.",
        ],
      },
      {
        heading: "On a title's page",
        bullets: [
          "▶ Play, or Resume when you have watched some of it.",
          "Watch Trailer opens the trailer full screen over the page; Back closes it.",
          "For a series, the season and episode list — pick an episode to play it. Play next continues through the episodes from there.",
          "Directed by, cast photos and the ratings, from open sources where your provider sends none.",
          "☆ / ★ Notify me when this title airs on any channel tracks it, and the bell alerts you when it does. The same button stops the alerts.",
          "You May Also Like / Similar Titles — more from the same kind of thing.",
          "In your playlist, or Not in your playlist when the title could not be matched to your provider — a discovery title is browsable but not playable.",
          "Mirrors: a channel with a backup feed linked to it plays from the backup when the main one fails.",
          "Close leaves the page; Back does the same.",
        ],
      },
    ],
  },
  {
    id: "search",
    title: "Search",
    summary: "One box for channels, the guide, movies, series and YouTube.",
    icon: ICONS.grid,
    sections: [
      {
        paragraphs: [
          "The search box sits in the top bar on every screen except Live TV, and there is a Search page of its own. It searches everything you have at once.",
        ],
      },
      {
        heading: "How it works",
        bullets: [
          "Type a few letters and the results appear as you type; there is no search button to press.",
          "The bar under the box narrows the result type: All, Channels, On the guide, Movies or Series.",
          "On the guide results are programmes: picking one tunes to the channel showing it.",
          "Search by voice uses the microphone where the device has one, and the same button stops listening.",
          "Clear search empties the box; Recent searches keeps what you looked for last, as one-press buttons.",
          "From YouTube searches YouTube itself for anything your own catalogue does not have. YouTube videos play in the app's own player where YouTube allows it.",
        ],
      },
      {
        heading: "If nothing comes up",
        bullets: [
          "Try fewer words — the search matches the whole phrase, not every word in it.",
          "Check the result type: a channel will not appear under Movies.",
          "Your provider's catalogue is only as good as their naming; a film may be filed under a more literal title.",
        ],
      },
    ],
  },
  {
    id: "mylist",
    title: "My List and favourites",
    summary: "Your own channel, movie and series list, and how to add to it.",
    icon: ICONS.star,
    sections: [
      {
        paragraphs: [
          "My List is your own shortlist of channels, movies and series — the thing you actually watch, in one place. Everything on it works with the arrow keys like the rows on Home do.",
        ],
      },
      {
        heading: "Adding something",
        bullets: [
          "On a channel's menu in the guide: Add channel to favorites (and Remove channel from favorites to undo it).",
          "The ☆/★ on your remote does the same for the channel you are watching, if you have not moved that button.",
          "On a movie or series page: the favorite button beside the title.",
          "Favorites are part of Pro. Anything you saved before stays on the list.",
        ],
      },
      {
        heading: "What it holds",
        bullets: [
          "Favorite Channels, Favorite Movies and Favorite Series, each with its count.",
          "Catch-up TV: watch what already aired, channel by channel — the shortcut to the Catch-up screen.",
          "Your list is empty until you add something, and it is kept per profile.",
        ],
      },
    ],
  },
  {
    id: "profiles",
    title: "Profiles",
    summary: "Up to five viewers, a Kids profile, and who is watching.",
    icon: ICONS.people,
    sections: [
      {
        paragraphs: [
          "Each profile keeps its own favourites, continue-watching, start screen and YouTube connection, so one viewer's list is not another's. Free has one profile; more are part of Pro.",
        ],
      },
      {
        heading: "Making one",
        bullets: [
          "Settings → Profiles → New Profile.",
          "Give it a Name and pick an Avatar, then Create Profile.",
          "Kids profile restricts the profile to the categories you have allowed, and leaving it asks for your parental PIN.",
          "Who's watching? is where you switch between them; the avatar at the bottom of the sidebar opens it too.",
          "Remove Active takes away the profile you are in. A protected profile asks for the parental PIN first.",
        ],
      },
    ],
  },
  {
    id: "parental",
    title: "Parental controls and the PIN",
    summary: "Hiding categories everywhere, and what the PIN guards.",
    icon: ICONS.lock,
    sections: [
      {
        paragraphs: [
          "Parental controls put whole categories of channels out of sight, everywhere in the app, behind a four-digit PIN. It is not a lock on what plays: it is a lock on what is shown at all.",
        ],
      },
      {
        heading: "Turning it on",
        bullets: [
          "Settings → Parental Controls. Enter a four-digit PIN, then the same PIN again to confirm.",
          "Mark a category Hidden and it disappears from the guide, search and the rows.",
          "Automatically hide categories that look adult marks the obvious ones for you.",
          "The list shows Visible, Hidden and Hidden (auto) beside each category, so you can see what the app did by itself.",
        ],
      },
      {
        heading: "What the PIN guards",
        bullets: [
          "Turning parental controls off again.",
          "Leaving a Kids profile, and removing a protected profile.",
          "Keep the PIN to yourself: there is no way to recover it except by removing the app's data.",
        ],
      },
    ],
  },
  {
    id: "keyboard",
    title: "Typing with a remote",
    summary: "Why the app has its own keyboard, and the setting that controls it.",
    icon: ICONS.keyboard,
    sections: [
      {
        paragraphs: [
          "A text box on a television has to be typed on with a remote, and plenty of TV boxes ignore the OK button when their own keyboard is up — you press OK and nothing is typed. So the app carries its own keyboard, where every key is a real button the arrows can reach.",
        ],
      },
      {
        heading: "Using it",
        bullets: [
          "Move with the arrows, press OK to type a letter.",
          "ABC / 123 switches between letters and numbers and symbols.",
          "Capitals shifts the letters; Space and Backspace do what they say.",
          "Close the keyboard puts it away without leaving the box you were typing in.",
          "It shows itself on a device whose own keyboard will not work, and stays out of the way everywhere else.",
        ],
      },
      {
        heading: "The setting",
        bullets: [
          "Settings → Text entry → App keyboard: When yours won't work (the default), Always, or Never.",
          "Always is the safe choice if typing is troublesome on your box — the arrow keys can always type on this keyboard.",
          "Never leaves typing entirely to your device.",
        ],
      },
    ],
  },
  {
    id: "your-country",
    title: "Put your country's channels first",
    summary: "Your country: whose channel groups lead the guide, Movies and Series, and fill Live Now.",
    icon: ICONS.home,
    sections: [
      {
        heading: "What it changes",
        bullets: [
          "The guide lists your country's channel groups first, then the groups with no country, then other countries'.",
          "Movies and Series send other countries' categories after yours.",
          "Live Now on Home shows channels from your country.",
          "It reads the country from the group and channel names your provider uses, such as \"UK|\" or \"USA\".",
        ],
      },
      {
        heading: "The setting",
        bullets: [
          "Settings → Your country → Country.",
          "Automatic, the default, follows your TV's region.",
          "It is saved on this device, for every profile.",
        ],
      },
    ],
  },
  {
    id: "launcher-mode",
    title: "Use Home as your TV's home screen",
    summary: "Launcher mode: your TV's apps, Quick settings and inputs at the top of Home.",
    icon: ICONS.home,
    sections: [
      {
        heading: "What it adds",
        bullets: [
          "Word links beside search: your box's Quick settings, Android settings and any inputs that are plugged in.",
          "An Apps row under the banner with every app on this box, phone apps included, plus tiles to get more apps from the stores you have.",
          "The rest of Home stays as it was, under the Apps row.",
        ],
      },
      {
        heading: "Turning it on or off",
        bullets: [
          "Settings → Launcher mode → Use Home as the TV's home screen.",
          "It appears only on Android TV boxes, where the app can open other apps.",
          "Off, Home is exactly as it always was.",
        ],
      },
      {
        heading: "Back and Home",
        bullets: [
          "With Launcher mode on, Back with nothing left to close takes you to the top of Home instead of asking to leave.",
          "Back from live TV still goes to the guide first, and from the guide to Home.",
          "Your remote's own Home button still opens the box's home screen.",
        ],
      },
    ],
  },
  {
    id: "remote-control",
    title: "Change what a remote button does",
    summary: "Press and long press, per button — part of Pro.",
    icon: ICONS.remote,
    sections: [
      {
        paragraphs: [
          "Settings → Remote control lists every button the app understands. Each one has a Press and a Long press, and each can be set to any action, or to Not set so it does nothing. Remote control settings are part of Pro.",
        ],
      },
      {
        heading: "How to change one",
        bullets: [
          "Choose Press or Long press on the button's row, then pick an action from the list.",
          "In the menus: ... on each row shows what the button does on the pages — that part is fixed, so navigation never breaks.",
          "Choose the row's Default entry to put the button back, or reset everything to the way the app ships.",
        ],
      },
      {
        heading: "Two rules the app keeps",
        bullets: [
          "Back always steps back. It is how you get out of anything, so it is not re-assignable.",
          "The arrows and OK only change while you are watching something full screen. Everywhere else they navigate, and a button's own default keeps working until you change it.",
        ],
      },
    ],
  },
  {
    id: "phone-remote",
    title: "Phone remote and second screen",
    summary: "Use a phone as a remote, on your own Wi-Fi — part of Pro.",
    icon: ICONS.phone,
    sections: [
      {
        paragraphs: [
          "A phone, tablet or laptop on the same Wi-Fi can be a remote and a second screen for the TV: buttons, typing with a real keyboard, the channel list, search and favourites. It works on your home network only, and only for devices you pair. It is part of Pro.",
        ],
      },
      {
        heading: "Pairing one",
        bullets: [
          "Settings → Phone remote → Pair a phone. A QR code and a short code appear on the TV.",
          "On a phone: scan the code with the camera. On a laptop or tablet: open the address shown and type the code.",
          "The code is good for a minute or so. Both devices have to be on the same Wi-Fi.",
          "A device that asks without a code makes the TV ask Allow … to control this TV? — nothing connects unless you accept it there.",
        ],
      },
      {
        heading: "Managing what is paired",
        bullets: [
          "Paired devices are listed with connected now, or when each was last used.",
          "Remove forgets a device (press it twice), and Turn off stops the remote listening altogether.",
        ],
      },
    ],
  },
  {
    id: "tv-start",
    title: "Opening straight into Live TV",
    summary: "Start on your last channel, and open the app when the box starts.",
    icon: ICONS.tv,
    sections: [
      {
        paragraphs: [
          "A TV that boots to a menu is not a TV. The app can open on the last channel you were watching, full screen, with the guide one Back away.",
        ],
      },
      {
        heading: "Start on",
        bullets: [
          "Settings → Home → Start on: Live TV (it is per profile).",
          "Live TV opens full screen on your last channel. Back goes to the guide, and Back again to Home.",
          "While it tunes, a card names the channel with Tuning…, or says Starting Live TV…, so there is never a black screen or a flash of the guide.",
        ],
      },
      {
        heading: "Opening when the box starts",
        bullets: [
          "Settings → Home → Open when the box starts makes the app start by itself after the box restarts or loses power.",
          "It may ask for Android's Display over other apps permission once; the app explains and opens the right screen.",
          "With Start on: Live TV the app goes straight to your last channel.",
          "Most boxes only go to standby when the TV is switched off, so this handles a restart or a power cut, not an ordinary evening.",
          "A box that does not allow apps to open by themselves says so instead of offering the switch.",
        ],
      },
    ],
  },
  {
    id: "notifications",
    title: "Alerts",
    summary: "Told when a favourite channel's programme starts, or a tracked title airs.",
    icon: ICONS.bell,
    sections: [
      {
        paragraphs: [
          "The app can tell you when something is about to start, so a programme is caught at the start instead of twenty minutes in.",
        ],
      },
      {
        heading: "Turning them on",
        bullets: [
          "Settings → Notifications → Alerts: Off, or On.",
          "On Android the app asks for notification permission the first time. Alerts always reach the bell in the top bar, even if Android's pop-ups are off.",
          "Alerts are checked every minute while the app is open.",
        ],
      },
      {
        heading: "What you get told about",
        bullets: [
          "A programme about to start on a favourite channel.",
          "A title you tracked (Track on a movie, series or programme) airing on any channel.",
          "The bell in the top bar counts what you have not read yet; each alert can be dismissed on its own.",
          "Tracked titles are listed in Settings → Notifications, with Remove beside each one.",
        ],
      },
    ],
  },
  {
    id: "sync",
    title: "Keeping the guide and catalogue fresh",
    summary: "Refresh frequency, Refresh now, and the channel health report.",
    icon: ICONS.sync,
    sections: [
      {
        paragraphs: [
          "Channels, movies, series and the guide refresh in the background while the app is open. Settings → Sync & Health is where you see when it last happened and ask for it now.",
        ],
      },
      {
        heading: "The controls",
        bullets: [
          "Refresh frequency — Off, 12h, 24h or 36h between automatic refreshes.",
          "Last updated — when it last ran.",
          "Refresh now — pulls channels, movies, series and the guide again and probes channel health. It all happens in the background; playback keeps running.",
          "Channel status — which channels are failing and why: not responding, or unreliable. Nothing is failing right now means all good.",
        ],
      },
      {
        heading: "Channels that come and go",
        bullets: [
          "A channel that fails twice in a row is hidden by itself, and comes back by itself when it works again.",
          "A channel you hid yourself stays hidden, so the app never overrules you.",
          "A dead primary still plays through a backup feed if you linked a mirror for that channel.",
        ],
      },
    ],
  },
  {
    id: "free-paid",
    title: "Free watching and Pro",
    summary: "What free includes, what Pro adds, and how to unlock it.",
    icon: ICONS.card,
    sections: [
      {
        paragraphs: [
          "Free watching works out of the box, with no account and nothing to sign up for. There is a daily allowance on films and series, and live TV has none.",
        ],
      },
      {
        heading: "The free allowance (per device, resets at midnight)",
        bullets: [
          "Live TV — unlimited.",
          "Movies — about two and a half hours a day, enough for a full film.",
          "Series — about half an hour a day.",
          "When the time is used up, the picture says so and offers Upgrade or Not now. Nothing is lost: it starts again the next day.",
        ],
      },
      {
        heading: "What Pro adds",
        bullets: [
          "Unlimited films and series.",
          "Catch-up, and recordings.",
          "Pause and rewind live TV.",
          "Multiview — four channels at once.",
          "Your own favourites and My List.",
          "The phone remote, and changing what your remote's buttons do.",
          "Up to five profiles and more than one source. Free includes one of each.",
          "Picture in picture, parental controls, Social and search stay free.",
        ],
      },
      {
        heading: "Unlocking it, and the price",
        bullets: [
          "Upgrade to Pro: $1.99 a month, $9.99 a year or $24.99 once.",
          "Until payments arrive, the published app comes with Pro switched on. Google Play billing and in-app payment replace that before launch.",
          "Locked features are never hidden — the button is there, and pressing it explains what it is part of rather than doing nothing.",
        ],
      },
    ],
  },
  {
    id: "sleep",
    title: "Sleep timer and still watching",
    summary: "Stop playback after a set time, or after nobody has touched anything.",
    icon: ICONS.clock,
    sections: [
      {
        paragraphs: [
          "Two separate things stop a TV nobody is watching, and both are in Settings → Player.",
        ],
      },
      {
        heading: "Sleep timer",
        bullets: [
          `Choose Off, or ${sleepChoicesList()}, from Settings → Player or the sleep timer at the bottom of the player's gear menu.`,
          "A minute before it stops, a box warns you and offers Keep watching, which adds time instead of stopping.",
          "The player shows the time left in the corner (Sleep 42 min).",
          "It survives closing the app, so a timer set for tonight is still there if you come back — but a timer that ran out while the app was closed is not carried into the next evening.",
        ],
      },
      {
        heading: "Ask if I'm still watching",
        bullets: [
          "Off by default, or after 2 hours, or after 4 hours with no button pressed while something is playing.",
          "When the time comes, Still watching? appears with Stop now and Keep watching, and stops after a minute with no answer.",
          "It frees your provider connection instead of holding a stream open all night, which is what a forgotten TV would otherwise do.",
        ],
      },
    ],
  },
  {
    id: "social",
    title: "Social and YouTube",
    summary: "Your YouTube subscriptions as rows, and playing them.",
    icon: ICONS.people,
    sections: [
      {
        paragraphs: [
          "Social brings your own YouTube subscriptions into the app as rows, so the creators you follow sit beside your channels.",
        ],
      },
      {
        heading: "Connecting it",
        bullets: [
          "Social → Connect YouTube shows a QR code; the code signs in on a phone, so no password is ever typed on the TV.",
          "It connects per profile, so each viewer has their own rows.",
          "The app only reads the channels you subscribe to. It never posts, likes or subscribes.",
          "Kids profiles cannot connect at all.",
        ],
      },
      {
        heading: "What it shows",
        bullets: [
          "Rows of the channels you subscribe to, with their recent videos, and a Live now row for anything streaming.",
          "YouTube results also appear in Search, under From YouTube.",
          "A video YouTube will not allow in the app says Can't play here rather than failing quietly.",
        ],
      },
      {
        heading: "Disconnecting",
        bullets: [
          "Settings → YouTube account shows who is connected, with Disconnect. It asks first, then removes the app's access at Google and forgets the sign-in on this TV.",
          "You can connect again from the Social page whenever you want.",
        ],
      },
    ],
  },
  {
    id: "troubleshoot",
    title: "When something goes wrong",
    summary: "Channels that stutter, silence, missing subtitles, a slow guide.",
    icon: ICONS.alert,
    sections: [
      {
        paragraphs: [
          "Most trouble is one of five things. Here is what to try, in the order worth trying it.",
        ],
      },
      {
        heading: "A channel will not start, or sounds and looks broken",
        bullets: [
          "The picture itself says what went wrong. Press ↻ Retry stream to try again — providers drop a stream for a moment often enough that one retry fixes most of it.",
          "If it says your provider's stream limit is reached, another device (or a recording) is using the account's streams.",
          "If that channel has a backup feed, playing it goes through the mirror instead.",
        ],
      },
      {
        heading: "Channels stutter, or the picture jerks",
        bullets: [
          "Try Settings → Player → Smooth HD playback (tunnelled video). It lets the TV's chip play the video directly, which fixes some HD channels, especially 1080i, on boxes that stutter.",
          "If a channel then shows no picture or sound, turn it off again: a few boxes do not like it.",
          "720p channels are the lightest to decode. A box that struggles with one provider's 1080i may be perfectly smooth on the same channel in 720p if the provider offers both.",
        ],
      },
      {
        heading: "No sound, or the wrong language",
        bullets: [
          "The gear ⚙ menu lists every sound track the stream carries — pick another one. It is remembered for that channel.",
          "If the menu shows only one track, the provider is sending only one.",
          "Sound used to drop out on some channels because of the AC3 and E-AC3 audio providers use; the app decodes those itself now.",
        ],
      },
      {
        heading: "No subtitles",
        bullets: [
          "Subtitles comes from the stream: if the gear menu says This stream carries no subtitles, the provider is not sending any for that channel.",
          "Some streams carry them only in one language, and some only on the catch-up copy rather than live.",
        ],
      },
      {
        heading: "The guide is empty, short, or slow to fill",
        bullets: [
          "A new install takes a while: a first guide download can be a couple of minutes, with Loading the guide… and a progress bar at the top. Leave the app open.",
          "Settings → Sync & Health → Refresh now fetches it again.",
          "If the guide is still thin, the provider's own guide feed may be the problem, not the app.",
        ],
      },
      {
        heading: "The app is slow to respond",
        bullets: [
          "Settings → Home: turn the banner trailer, the Live Now row and the rotating featured title off, and set Titles per row to 12. On a small box these are the heaviest things the app draws.",
          "Close the app fully now and then — a TV box keeps it in memory for days otherwise.",
        ],
      },
      {
        heading: "Nothing plays at all, or your source errors",
        bullets: [
          "Settings → Source shows your connections and whether each one works. Check the server address, username and password first.",
          "A lapsed subscription shows its errors in place — you are not put back through setup, and nothing needs re-entering.",
        ],
      },
    ],
  },
  {
    id: "about",
    title: "About and where the data comes from",
    summary: "Credits, licences, and what the app sends where.",
    icon: ICONS.sign,
    sections: [
      {
        paragraphs: [
          "Settings → About names the app and credits everything it shows that it did not make itself.",
        ],
      },
      {
        heading: "Where things come from",
        bullets: [
          "Your channels, movies, series, artwork, descriptions, cast and ratings come from your own provider.",
          "Titles, genres, directors and cast details: Wikidata. Trending and popular: Wikipedia page views. Box-office number ones: Wikipedia.",
          "TV schedules and show pictures: TVmaze. Cast photos: Wikimedia Commons, each under its own licence.",
          "Settings → About → Image credits lists each photo's credit, one line at a time.",
        ],
      },
      {
        heading: "Privacy in short",
        bullets: [
          "Your provider details are stored on this device and sent only to your provider.",
          "Connecting YouTube is optional, per profile, and read-only.",
          "The phone remote listens on your own Wi-Fi only, and only for devices you paired.",
        ],
      },
    ],
  },
];

/** Every word of a topic, for searching and for the tests. */
export function topicText(topic: HelpTopic): string {
  const parts: string[] = [topic.title, topic.summary];
  for (const section of topic.sections) {
    if (section.heading) parts.push(section.heading);
    if (section.paragraphs) parts.push(...section.paragraphs);
    if (section.bullets) parts.push(...section.bullets);
  }
  return parts.join("\n");
}

/** The topics whose title, summary or words contain the query. Empty query: all of them. */
export function filterTopics(query: string): readonly HelpTopic[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return HELP_TOPICS;
  return HELP_TOPICS.filter((topic) => topicText(topic).toLowerCase().includes(needle));
}
